import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Glyph } from "./icons";
import { datasetSamplesQueryOptions, useSampleImage, useVisiblePreview, type PreviewSample } from "./data-preview/useDatasetPreview";
import { datasetPreviewSources, type PreviewSource } from "@/lib/dataset-preview";
import "./project-mosaic.css";

/** Project identity uses distinct datasets, never a dataset's version caption. */
export function ProjectMosaic({ slug, compact = false }: { slug: string; compact?: boolean }) {
  const { ref, visible } = useVisiblePreview();
  const catalog = api.useQuery("get", "/api/projects/{slug}/datasets", { params: { path: { slug } } },
    { enabled: visible, staleTime: 60_000, retry: false });
  // TEST samples are admin-only, so a member's mosaic would only collect 403s; the viewer's role is unknown for other projects.
  const datasets = [...(catalog.data?.datasets ?? [])].filter(dataset => dataset.category !== "frozen_test" && datasetPreviewSources(dataset).length)
    .sort((a, b) => a.name.localeCompare(b.name)).slice(0, compact ? 4 : 12);
  const capacity = compact ? 4 : 12;
  const sources = datasets.map(dataset => {
    const available = datasetPreviewSources(dataset);
    return available.find(source => source.kind === "version") ?? available[0];
  });
  // Read a bounded pool per dataset; only the selected cells fetch image blobs.
  const reads = useQueries({ queries: datasets.map((dataset, index) =>
    datasetSamplesQueryOptions(slug, dataset.name, sources[index], capacity, {}, visible)) });
  const tiles: { dataset: typeof datasets[number]; source: PreviewSource; sample: PreviewSample }[] = [];
  const seen = new Set<string>();
  // Round-robin gives every dataset a turn, then fills spare cells with distinct images.
  for (let offset = 0; offset < capacity && tiles.length < capacity; offset++) {
    for (let index = 0; index < datasets.length && tiles.length < capacity; index++) {
      const sample = reads[index].data?.samples[offset];
      if (!sample || seen.has(sample.sample_sha256)) continue;
      seen.add(sample.sample_sha256);
      tiles.push({ dataset: datasets[index], source: sources[index], sample });
    }
  }
  const pending = catalog.isLoading || reads.some(read => read.isLoading);
  const failed = catalog.isError || reads.some(read => read.isError);
  const emptyLabel = pending ? "Loading images…" : failed ? "Images unavailable" : "No images yet";
  return <div ref={ref} className={`project-mosaic${compact ? " is-compact" : ""}`} data-testid="project-mosaic"
    aria-label={compact ? "Project image preview" : `Project preview: ${tiles.length} images`} aria-busy={pending}>
    {tiles.length ? tiles.map(({ dataset, source, sample }) => <div className="project-mosaic-tile" key={sample.sample_sha256}
      title={dataset.display_name || dataset.name} data-dataset={dataset.name}>
      <TileImage slug={slug} name={dataset.name} source={source} sample={sample} label={dataset.display_name || dataset.name} />
    </div>) : <div className="project-mosaic-empty" title={emptyLabel}>
      <Glyph name={failed ? "image_not_supported" : "folder_open"} size={compact ? 18 : 24} />
      {!compact && <span>{emptyLabel}</span>}
    </div>}
  </div>;
}

function TileImage({ slug, name, source, sample, label }: { slug: string; name: string; source: PreviewSource; sample: PreviewSample; label: string }) {
  const image = useSampleImage(slug, name, source, sample.sample_sha256, true, "thumbnail");
  const [failed, setFailed] = useState(false);
  return image.url && !failed ? <img src={image.url} alt={label} loading="lazy" decoding="async" onError={() => setFailed(true)} />
    : <Glyph name={image.error || failed ? "image_not_supported" : "photo_library"} size={16} />;
}
